// src/components/lead/CompactLeadView.tsx
import type { ExtractedLeadV2 } from "../../types/lead";

type LeadLike = {
  name: string;
  extracted: ExtractedLeadV2 | null;
};

type Props = {
  currentLead: LeadLike;
  leadStatusClass: string;
  leadStatusLabel: string;
  leadAgeDisplay: string;
};

export function CompactLeadView(props: Props) {
  const { currentLead, leadStatusClass, leadStatusLabel, leadAgeDisplay } = props;

  const e = currentLead.extracted;

  const interestBadge = (v?: string | null) => {
    const s = (v || "").trim().toLowerCase();
    if (!s || s === "unknown") return null;
    if (s === "hot") return "Hot";
    if (s === "warm") return "Warm";
    if (s === "cold") return "Cold";
    return s.replace(/_/g, " ");
  };

  const prettyTimeline = (v?: string | null) => {
    const s = (v || "").trim().toLowerCase();
    if (!s || s === "unknown") return null;
    return s.replace(/_/g, " ");
  };

  const interestText = interestBadge(e?.interest_level);
  const timelineText = prettyTimeline(e?.ready_to_buy_timeline);

  return (
    <div className="bg-gray-50 rounded-2xl p-4 border border-gray-200 text-left text-sm sm:text-base"> {/* Compact view summary card */}
      <div className="flex items-start justify-between mb-2 gap-2"> {/* Header row */}
        <div className="min-w-0"> {/* Left side: name + product */}
          <div className="text-[11px] uppercase tracking-wide text-gray-500">
            Quick summary
          </div>
          <div className="font-semibold text-base sm:text-lg truncate">
            {currentLead.extracted?.first_name || currentLead.extracted?.last_name
              ? `${currentLead.extracted?.first_name ?? ""} ${
                  currentLead.extracted?.last_name ?? ""
                }`.trim()
              : currentLead.name || "New customer"}
          </div>
          {(e?.product_line || e?.product_subtype || e?.enunciated_budget) && (
            <div className="text-xs text-gray-500 mt-0.5 truncate">
              {e?.product_line || "Sheds"}
              {e?.product_subtype ? ` • ${e.product_subtype}` : ""}
              {e?.enunciated_budget ? ` • Budget: ${e.enunciated_budget}` : ""}
            </div>
          )}
        </div> {/* Close left side: name + product */}
        <div className="flex flex-col items-end gap-1"> {/* Right side: status + age */}
          <span className={leadStatusClass}>{leadStatusLabel}</span>
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-white text-gray-600 border border-gray-200">
            Age: {leadAgeDisplay}
          </span>
        </div> {/* Close right side: status + age */}
      </div> {/* Close header row */}

      <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-[13px] sm:text-sm"> {/* Details grid */}
        {e?.phone && (
          <div>
            <span className="font-semibold">Phone:</span>{" "}
            {e.phone}
          </div>
        )}

        {e?.email && (
          <div>
            <span className="font-semibold">Email:</span>{" "}
            {e.email}
          </div>
        )}

        {e?.customer_city_state && (
          <div>
            <span className="font-semibold">City:</span>{" "}
            {e.customer_city_state}
          </div>
        )}

        {interestText && (
          <div>
            <span className="font-semibold">Interest:</span> {interestText}
          </div>
        )}

        {timelineText && (
          <div>
            <span className="font-semibold">Timeline:</span> {timelineText}
          </div>
        )}
      </div>
    </div>
  );
}
